import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { SiaActionsService } from '@sia/actions';
import {
  SelectBoxMultiComponent,
  SiaCheckBoxComponent,
  SiaSelectBoxComponent,
  SiaTextBoxComponent,
} from '@sia/inputs';
import {
  FileService,
  jsonDeepClone,
  notify,
  SiaComponentConfigPerspectives,
  SiaComponentConfigService,
  SiaComponentService,
  SiaNewRouterService,
  SiaSessionService,
  SiaTableInformations,
  SiaTranslatePipe,
} from '@sia/shared';
import { Subscription } from 'rxjs';
import {
  SiaMultiGrid,
  SiaMultiGridConfiguration,
} from '../../interfaces/sia-multi-grid.configuration';

@Component({
  selector: 'sia-multi-grid-configuration',
  standalone: true,
  imports: [
    MatButtonModule,
    SiaCheckBoxComponent,
    SiaTextBoxComponent,
    MatIcon,
    SiaSelectBoxComponent,
    SiaTranslatePipe,
    DragDropModule,
    MatDivider,
    SelectBoxMultiComponent,
  ],
  templateUrl: './sia-multi-grid-configuration.component.html',
  styleUrl: './sia-multi-grid-configuration.component.scss',
})
export class SiaMultiGridConfigurationComponent {
  private siaComponentConfigService = inject(SiaComponentConfigService);
  private siaComponentService = inject(SiaComponentService);
  private siaRouterService = inject(SiaNewRouterService);
  private siaSessionService = inject(SiaSessionService);
  private siaActinService = inject(SiaActionsService);
  #fileService = inject(FileService);

  private _gridId: string = '';
  configuration: SiaMultiGridConfiguration = {};
  perspective: SiaComponentConfigPerspectives = {};
  gridProperties: { id: string; description: string }[] = [];
  private _saving: boolean = false;
  private _subs: Subscription[] = [];

  /** Inserted by Angular inject() migration for backwards compatibility */

  /**
   *
   */
  constructor() {
    let s0 = this.siaActinService.actionFired$().subscribe((action) => {
      switch (action.action?.id) {
        case 'save':
          this.savePerspective();
          break;
        case 'download-perspective':
          this.#fileService.downloadPerspective(
            this._gridId || '',
            'multi-grid',
          );
          break;
        default:
          break;
      }
    });
    this._subs.push(s0);
  }
  async savePerspective() {
    if (this._saving) {
      return;
    }
    this._saving = true;
    let perspectiveConfig: SiaMultiGridConfiguration = jsonDeepClone(
      this.configuration,
    );

    perspectiveConfig.grids?.map((grid) => (grid.dataFields = undefined));
    this.perspective.jsonConfig = JSON.stringify(perspectiveConfig);
    var saved = true;
    if (this.perspective.id) {
      let updatedConfig =
        (await this.siaComponentConfigService.updateComponentPerspective(
          this.perspective,
        )) ?? this.perspective;
      saved = updatedConfig?.id === this.perspective.id;
      this.perspective = updatedConfig;
    } else {
      this.perspective =
        await this.siaComponentConfigService.setComponentPerspective(
          this.perspective,
        );
    }

    if (saved) {
      notify('Configurazione salvata con successo', 1500, 'success');
    }
    this._saving = false;
  }

  addGrid() {
    this.configuration.grids = this.configuration.grids || [];
    this.configuration.grids.push({});
  }

  removeGrid(index: number) {
    this.configuration.grids?.splice(index, 1);
  }

  onGridChanged(grid: SiaMultiGrid) {
    if (!grid.gridId) {
      return;
    }
    this.siaComponentService
      .getViewInformations(grid.gridId ?? '')
      .then((info: SiaTableInformations[]) => {
        let fields: any[] = [];
        info.map((x) => {
          if (x.columnName) {
            fields.push({
              id: x.columnName.toLocaleLowerCase(),
              description: x.columnName.toLocaleLowerCase(),
              selected: false,
            });
          }
        });
        grid.dataFields = fields;
      });
  }
  onDrop(event: CdkDragDrop<any, any>) {
    if (!this.configuration.grids) {
      return;
    }
    moveItemInArray(
      this.configuration.grids,
      event.previousIndex,
      event.currentIndex,
    );
  }

  ngAfterViewInit() {
    let gridId = this.siaSessionService.getSessionParamValue('gridId');
    if (!gridId) {
      this.siaRouterService.navigateBack();
    }
    this._gridId = gridId;
    this.retriveConfiguration();
  }

  ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }
  addBindingProperty(grid: SiaMultiGrid) {
    grid.bindingProperties = grid.bindingProperties || [];
    grid.bindingProperties.push({ propertyName: '', bindingName: '' });
  }

  addSessionProperty(grid: SiaMultiGrid) {
    grid.sessionProperties = grid.sessionProperties || [];
    grid.sessionProperties.push({ propertyName: '', bindingName: '' });
  }

  removeSessionProperty(grid: SiaMultiGrid, index: number) {
    grid.sessionProperties?.splice(index, 1);
  }
  removeBindingProperty(grid: SiaMultiGrid, index: number) {
    grid.bindingProperties?.splice(index, 1);
    this.setGridProperties();
  }
  setGridProperties() {
    let gridProperties: { id: string; description: string }[] = [];
    this.configuration.grids?.forEach((grid) => {
      if (grid.bindingProperties) {
        grid.bindingProperties.forEach((bindingProperty) => {
          gridProperties.push({
            id: bindingProperty.bindingName,
            description: bindingProperty.bindingName,
          });
        });
      }
    });
    this.gridProperties = gridProperties;
  }

  async retriveConfiguration() {
    let perspective =
      await this.siaComponentConfigService.getDefaultPerspective(
        'multi-grid',
        this._gridId,
        'MASTER',
      );
    if (perspective) {
      this.perspective = perspective;
    } else {
      this.perspective = {
        componentId: this._gridId,
        configType: 'multi-grid',
        description: 'DEFAULT',
        defaultMode: 'A',
        jsonConfig: '{}',
      };
    }
    this.configuration = JSON.parse(this.perspective.jsonConfig ?? '{}');
    // console.log(jsonDeepClone(this.configuration));
    this.setGridProperties();
  }
}
